import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatReleaseDate } from "../lib/format";
import { useWallet } from "../services/wallet.service";

type Props = {
  reference: string;
  title: string;
  lines: string[];
  cashPaid: number;
  currency?: string;
  pointsUsed: number;
  pointsEarned: number;
  earnPending: boolean;
  earnAvailableAt: string | null;
  tierBefore: string;
  tierAfter: string;
};

export function BookingConfirmed({
  reference,
  title,
  lines,
  cashPaid,
  currency = "AED",
  pointsUsed,
  pointsEarned,
  earnPending,
  earnAvailableAt,
  tierBefore,
  tierAfter,
}: Props) {
  const wallet = useWallet();
  const releaseOn = formatReleaseDate(earnAvailableAt);
  const earnNote = pointsEarned <= 0
    ? "This booking did not earn points."
    : earnPending
      ? `These points are pending${releaseOn ? ` until ${releaseOn}` : ""}. They are not spendable until then.`
      : "These points are in your available balance.";
  return (
    <div className="mx-auto max-w-xl rounded-lg border border-[#e7e7e7] bg-white p-6 sm:p-8">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#e8f6ea] text-[#008009]">
        <Check className="h-6 w-6" />
      </div>
      <p className="mt-4 text-sm font-semibold text-[#008009]">Booking confirmed</p>
      <h1 className="mt-1 text-2xl font-bold text-[#1a1a1a]">{title}</h1>
      <p className="mt-2 text-sm text-[#595959]">
        Reference <span className="font-semibold text-[#1a1a1a]">{reference}</span>
      </p>
      <ul className="mt-4 space-y-1 text-sm text-[#1a1a1a]">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      <dl className="mt-5 space-y-2 border-t border-[#e7e7e7] pt-4 text-sm">
        <div className="flex justify-between">
          <dt className="text-[#595959]">Cash paid</dt>
          <dd className="font-semibold">
            {new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 2 }).format(cashPaid)}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-[#595959]">Points used</dt>
          <dd className="font-semibold">{pointsUsed.toLocaleString("en-IN")}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-[#595959]">Points earned</dt>
          <dd className="font-semibold text-[#008009]">
            +{pointsEarned.toLocaleString("en-IN")}
            {earnPending ? " pending" : ""}
          </dd>
        </div>
      </dl>
      <p className="mt-3 text-sm text-[#595959]">{earnNote}</p>
      <div className="mt-4 rounded-md bg-[#f5f5f5] px-3 py-3 text-sm">
        <p className="font-semibold text-[#1a1a1a]">
          {wallet.points.toLocaleString("en-IN")} available
          {wallet.pendingPoints > 0 ? ` · ${wallet.pendingPoints.toLocaleString("en-IN")} pending` : ""}
        </p>
        <p className="mt-1 text-[#595959]">
          {wallet.tierName}
          {wallet.benefits.length ? ` · ${wallet.benefits.slice(0, 2).join(", ")}` : ""}
        </p>
      </div>
      {tierAfter !== tierBefore ? (
        <p className="mt-4 rounded-md bg-[#fff6d6] px-3 py-2 text-sm font-medium text-[#1a1a1a]">
          This booking moved you to {tierAfter}.
        </p>
      ) : null}
      <div className="mt-6 flex flex-wrap gap-3">
        <Button asChild>
          <Link to="/member/trips">View trips</Link>
        </Button>
        <Button asChild variant="outline">
          <Link to="/member/rewards">See rewards</Link>
        </Button>
        <Button asChild variant="ghost">
          <Link to="/member">Book another</Link>
        </Button>
      </div>
    </div>
  );
}
